import { useEffect, useMemo, useState } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

import {
  ArrowLeft,
  Check,
  ClipboardCheck,
  Loader2,
  Play,
  Save,
  Users,
  CheckCircle2
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

function lista(data, chaves = []) {
  if (Array.isArray(data)) {
    return data;
  }

  for (const chave of chaves) {
    if (Array.isArray(data?.[chave])) {
      return data[chave];
    }
  }

  return [];
}

function nomeAtleta(atleta) {
  return (
    atleta?.aluno_nome ||
    atleta?.nome ||
    atleta?.nome_completo ||
    atleta?.atleta_nome ||
    'Atleta'
  );
}

function idAtleta(atleta) {
  return (
    atleta?.aluno_id ||
    atleta?.atleta_id ||
    atleta?.id
  );
}

export default function SeletivaProvaOperacao({
  seletiva,
  prova,
  onVoltar,
  onAtualizar
}) {
  const [loading, setLoading] = useState(true);
  const [acao, setAcao] = useState(false);

  const [painelProva, setPainelProva] = useState(null);
  const [rodadas, setRodadas] = useState([]);
  const [rodada, setRodada] = useState(null);
  const [resultados, setResultados] = useState([]);

  const [selecionados, setSelecionados] = useState([]);
  const [reavaliarIds, setReavaliarIds] = useState([]);
  const [grupoSelecionado, setGrupoSelecionado] = useState('');
  const [valores, setValores] = useState({});

  const carregar = async () => {
    if (!seletiva?.id || !prova?.id) {
      return;
    }

    setLoading(true);

    try {
      const [painelResp, rodadasResp] =
        await Promise.all([
          api.get(
            `/seletivas/${seletiva.id}/painel`
          ),
          api.get(
            `/seletivas/${seletiva.id}/provas/${prova.id}/rodadas`
          )
        ]);

      const provasPainel =
        painelResp.data?.provas || [];

      const atual =
        provasPainel.find(
          (item) => item.id === prova.id
        ) || prova;

      setPainelProva(atual);

      const listaRodadas = lista(
        rodadasResp.data,
        ['rodadas', 'itens']
      );

      setRodadas(listaRodadas);

      const ativa =
        listaRodadas.find(
          (item) =>
            item.status === 'em_andamento'
        ) ||
        listaRodadas.find(
          (item) =>
            item.status === 'aberta'
        ) ||
        null;

      setRodada(ativa);

      if (ativa?.id) {
        const resultadosResp =
          await api.get(
            `/seletivas/${seletiva.id}/provas/${prova.id}/rodadas/${ativa.id}/resultados`
          );

        setResultados(
          lista(
            resultadosResp.data,
            ['resultados']
          )
        );
      } else {
        setResultados([]);
      }
    } catch (err) {
      toast.error(
        formatApiError(err)
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, [seletiva?.id, prova?.id]);

  const atletas = useMemo(
    () => painelProva?.atletas || [],
    [painelProva]
  );

  const atletasDisponiveis = useMemo(
    () =>
      atletas.filter((atleta) => {
        const status =
          atleta.situacao ||
          atleta.status ||
          '';

        return (
          status === 'pendente' ||
          status === 'avaliado' ||
          status === 'em_reavaliacao'
        );
      }),
    [atletas]
  );

  const gruposDisponiveis = useMemo(
    () =>
      [...new Set(
        atletas
          .map((atleta) =>
            String(atleta.grupo || '')
              .trim()
              .toUpperCase()
          )
          .filter(Boolean)
      )].sort(),
    [atletas]
  );

  const atletasDoGrupo = useMemo(
    () =>
      grupoSelecionado
        ? atletasDisponiveis.filter(
            (atleta) =>
              String(atleta.grupo || '')
                .trim()
                .toUpperCase() === grupoSelecionado
          )
        : [],
    [
      atletasDisponiveis,
      grupoSelecionado
    ]
  );

  const selecionarGrupo = (grupo) => {
    const valor = String(grupo || '')
      .trim()
      .toUpperCase();

    setGrupoSelecionado(valor);

    if (!valor) {
      setSelecionados([]);
      setReavaliarIds([]);
      return;
    }

    const integrantes =
      atletasDisponiveis.filter(
        (atleta) =>
          String(atleta.grupo || '')
            .trim()
            .toUpperCase() === valor
      );

    const ids = integrantes
      .map((atleta) => idAtleta(atleta))
      .filter(Boolean);

    const idsReavaliacao = integrantes
      .filter(
        (atleta) =>
          (atleta.situacao || atleta.status) ===
          'avaliado'
      )
      .map((atleta) => idAtleta(atleta))
      .filter(Boolean);

    setSelecionados(ids);
    setReavaliarIds(idsReavaliacao);
  };

  const alternarAtleta = (atleta) => {
    const id = idAtleta(atleta);

    if (!id) {
      return;
    }

    const jaSelecionado =
      selecionados.includes(id);

    setSelecionados((atual) =>
      jaSelecionado
        ? atual.filter(
            (item) => item !== id
          )
        : [...atual, id]
    );

    if ((atleta.situacao || atleta.status) === 'avaliado') {
      setReavaliarIds((atual) => {
        if (jaSelecionado) {
          return atual.filter(
            (item) => item !== id
          );
        }

        if (atual.includes(id)) {
          return atual;
        }

        return [...atual, id];
      });
    }
  };

  const alterarValor = (
    atletaId,
    campo,
    valor
  ) => {
    setValores((atual) => ({
      ...atual,
      [atletaId]: {
        ...(atual[atletaId] || {}),
        [campo]: valor
      }
    }));
  };

  const montarTentativas = (
    atletaId
  ) => {
    const dados =
      valores[atletaId] || {};

    const quantidade =
      Math.max(
        1,
        Number(
          prova?.numero_tentativas || 1
        )
      );

    const tentativas = [];

    for (
      let numero = 1;
      numero <= quantidade;
      numero += 1
    ) {
      const chave =
        `tentativa_${numero}`;

      const valor =
        dados[chave];

      if (
        valor !== undefined &&
        valor !== ''
      ) {
        tentativas.push({
          numero,
          valor_numerico:
            Number(valor)
        });
      }
    }

    return tentativas;
  };

  const salvarResultado = async (
    atleta
  ) => {
    if (!rodada?.id) {
      return;
    }

    const atletaId =
      idAtleta(atleta);

    if (!atletaId) {
      return;
    }

    const tipo =
      prova?.tipo_resultado;

    const dados =
      valores[atletaId] || {};

    const payload = {
      atleta_id: atletaId,
      tentativas: [],
      valor_final_numerico: null,
      conceito_final: null,
      nota_final: null,
      observacao:
        dados.observacao || ''
    };

    if (
      [
        'tempo',
        'medida',
        'quantidade'
      ].includes(tipo)
    ) {
      payload.tentativas =
        montarTentativas(
          atletaId
        );

      if (
        payload.tentativas.length === 0
      ) {
        toast.error(
          `Informe o resultado de ${nomeAtleta(atleta)}.`
        );
        return;
      }
    }

    if (tipo === 'nota') {
      if (
        dados.nota === undefined ||
        dados.nota === ''
      ) {
        toast.error(
          `Informe a nota de ${nomeAtleta(atleta)}.`
        );
        return;
      }

      payload.nota_final =
        Number(dados.nota);
    }

    if (tipo === 'conceito') {
      if (!dados.conceito) {
        toast.error(
          `Informe o conceito de ${nomeAtleta(atleta)}.`
        );
        return;
      }

      payload.conceito_final =
        dados.conceito;
    }

    if (
      tipo === 'observacao' &&
      !payload.observacao.trim()
    ) {
      toast.error(
        `Informe a observação de ${nomeAtleta(atleta)}.`
      );
      return;
    }

    setAcao(true);

    try {
      await api.post(
        `/seletivas/${seletiva.id}/provas/${prova.id}/rodadas/${rodada.id}/resultados`,
        payload
      );

      toast.success(
        `${nomeAtleta(atleta)} avaliado`
      );

      const resp = await api.get(
        `/seletivas/${seletiva.id}/provas/${prova.id}/rodadas/${rodada.id}/resultados`
      );

      setResultados(
        lista(
          resp.data,
          ['resultados']
        )
      );

      if (onAtualizar) {
        await onAtualizar();
      }
    } catch (err) {
      toast.error(
        formatApiError(err)
      );
    } finally {
      setAcao(false);
    }
  };

  const finalizarRodada = async () => {
    if (!rodada?.id) {
      return;
    }

    setAcao(true);

    try {
      await api.post(
        `/seletivas/${seletiva.id}/provas/${prova.id}/rodadas/${rodada.id}/finalizar`
      );

      toast.success(
        'Rodada finalizada'
      );

      setRodada(null);
      setValores({});
      setResultados([]);

      await carregar();

      if (onAtualizar) {
        await onAtualizar();
      }
    } catch (err) {
      toast.error(
        formatApiError(err)
      );
    } finally {
      setAcao(false);
    }
  };

  const iniciarRodada = async () => {
    if (!rodada?.id) {
      return;
    }

    setAcao(true);

    try {
      const resp = await api.post(
        `/seletivas/${seletiva.id}/provas/${prova.id}/rodadas/${rodada.id}/iniciar`
      );

      setRodada(resp.data);

      toast.success(
        'Rodada iniciada'
      );

      await carregar();

      if (onAtualizar) {
        await onAtualizar();
      }
    } catch (err) {
      toast.error(
        formatApiError(err)
      );
    } finally {
      setAcao(false);
    }
  };

  const criarRodada = async () => {
    if (selecionados.length === 0) {
      toast.error(
        'Selecione pelo menos um atleta.'
      );
      return;
    }

    setAcao(true);

    try {
      const resp = await api.post(
        `/seletivas/${seletiva.id}/provas/${prova.id}/rodadas`,
        {
          atleta_ids: selecionados,
          reavaliar_ids:
            reavaliarIds.filter(
              (id) =>
                selecionados.includes(id)
            )
        }
      );

      setRodada(resp.data);

      setSelecionados([]);
      setReavaliarIds([]);
      setGrupoSelecionado('');

      toast.success(
        `Rodada ${resp.data?.numero_rodada || ''} criada`
      );

      await carregar();

      if (onAtualizar) {
        await onAtualizar();
      }
    } catch (err) {
      toast.error(
        formatApiError(err)
      );
    } finally {
      setAcao(false);
    }
  };

  const resultadoPorAtleta = useMemo(
    () => {
      const mapa = {};

      resultados.forEach((item) => {
        if (item.atleta_id) {
          mapa[item.atleta_id] = item;
        }
      });

      return mapa;
    },
    [resultados]
  );

  if (loading) {
    return (
      <div className="py-16 flex justify-center">
        <Loader2 className="w-7 h-7 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Button
        variant="ghost"
        onClick={onVoltar}
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Voltar para provas
      </Button>

      <Card>
        <CardContent className="p-5">
          <h2 className="text-xl font-bold">
            {prova?.nome || 'Prova'}
          </h2>

          <p className="text-sm text-zinc-400 mt-1">
            {seletiva?.nome || 'Seletiva'}
          </p>
        </CardContent>
      </Card>

      {rodada && (
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-semibold">
                  Rodada {rodada.numero_rodada}
                </div>

                <div className="text-sm text-zinc-400 mt-1">
                  {(rodada.atleta_ids || []).length} atleta(s)
                  {' • '}
                  {rodada.status === 'aberta'
                    ? 'Aguardando início'
                    : rodada.status === 'em_andamento'
                      ? 'Em andamento'
                      : 'Concluída'}
                </div>
              </div>

              {rodada.status === 'aberta' && (
                <Button
                  onClick={iniciarRodada}
                  disabled={acao}
                >
                  {acao ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4 mr-2" />
                  )}

                  Iniciar rodada
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {rodada?.status === 'em_andamento' && (
        <div className="space-y-3">
          {(rodada.atleta_ids || []).map((atletaId) => {
            const atleta =
              atletas.find(
                (item) =>
                  idAtleta(item) === atletaId
              ) || {
                aluno_id: atletaId,
                aluno_nome: atletaId
              };

            const salvo =
              resultadoPorAtleta[atletaId];

            const quantidade =
              Math.max(
                1,
                Number(
                  prova?.numero_tentativas || 1
                )
              );

            return (
              <Card key={atletaId}>
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-semibold">
                      {nomeAtleta(atleta)}
                    </div>

                    {salvo && (
                      <div className="text-sm flex items-center gap-1 text-emerald-500">
                        <CheckCircle2 className="w-4 h-4" />
                        Salvo
                      </div>
                    )}
                  </div>

                  {[
                    'tempo',
                    'medida',
                    'quantidade'
                  ].includes(
                    prova?.tipo_resultado
                  ) && (
                    <div className="grid sm:grid-cols-2 gap-3">
                      {Array.from(
                        { length: quantidade },
                        (_, index) => {
                          const numero =
                            index + 1;

                          const chave =
                            `tentativa_${numero}`;

                          return (
                            <div key={chave}>
                              <Label>
                                {quantidade > 1
                                  ? `Tentativa ${numero}`
                                  : 'Resultado'}

                                {prova?.unidade
                                  ? ` (${prova.unidade})`
                                  : ''}
                              </Label>

                              <Input
                                type="number"
                                step="any"
                                value={
                                  valores[
                                    atletaId
                                  ]?.[chave] || ''
                                }
                                onChange={(e) =>
                                  alterarValor(
                                    atletaId,
                                    chave,
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          );
                        }
                      )}
                    </div>
                  )}

                  {prova?.tipo_resultado === 'nota' && (
                    <div>
                      <Label>Nota</Label>

                      <Input
                        type="number"
                        step="any"
                        min={prova?.nota_minima}
                        max={prova?.nota_maxima}
                        value={
                          valores[
                            atletaId
                          ]?.nota || ''
                        }
                        onChange={(e) =>
                          alterarValor(
                            atletaId,
                            'nota',
                            e.target.value
                          )
                        }
                      />
                    </div>
                  )}

                  {prova?.tipo_resultado === 'conceito' && (
                    <div>
                      <Label>Conceito</Label>

                      <select
                        className="w-full h-10 rounded-md border bg-background px-3"
                        value={
                          valores[
                            atletaId
                          ]?.conceito || ''
                        }
                        onChange={(e) =>
                          alterarValor(
                            atletaId,
                            'conceito',
                            e.target.value
                          )
                        }
                      >
                        <option value="">
                          Selecione
                        </option>

                        {(prova?.conceitos || []).map(
                          (conceito) => (
                            <option
                              key={conceito}
                              value={conceito}
                            >
                              {conceito}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                  )}

                  <div>
                    <Label>
                      Observação
                    </Label>

                    <Textarea
                      value={
                        valores[
                          atletaId
                        ]?.observacao || ''
                      }
                      onChange={(e) =>
                        alterarValor(
                          atletaId,
                          'observacao',
                          e.target.value
                        )
                      }
                    />
                  </div>

                  <Button
                    onClick={() =>
                      salvarResultado(atleta)
                    }
                    disabled={acao}
                  >
                    <Save className="w-4 h-4 mr-2" />

                    {salvo
                      ? 'Atualizar resultado'
                      : 'Salvar resultado'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {rodada?.status === 'em_andamento' && (
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-semibold">
                  Finalizar rodada
                </div>

                <div className="text-sm text-zinc-400 mt-1">
                  {resultados.length} de {(rodada.atleta_ids || []).length} resultado(s) salvo(s)
                </div>
              </div>

              <Button
                onClick={finalizarRodada}
                disabled={
                  acao ||
                  resultados.length <
                    (rodada.atleta_ids || []).length
                }
              >
                {acao ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}

                Finalizar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!rodada && (
        <Card>
          <CardContent className="p-5 space-y-4">
            <div>
              <div className="font-semibold flex items-center gap-2">
                <Users className="w-5 h-5" />
                Próxima rodada
              </div>

              <p className="text-sm text-zinc-400 mt-1">
                Selecione os atletas que serão avaliados agora.
              </p>
            </div>

            {atletasDisponiveis.length === 0 ? (
              <div className="py-6 text-center text-zinc-400">
                Nenhum atleta disponível.
              </div>
            ) : gruposDisponiveis.length === 0 ? (
              <div className="py-6 text-center text-zinc-400">
                Nenhum grupo definido para esta seletiva.
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label>Grupo que chegou à estação</Label>

                  <select
                    className="w-full h-11 rounded-md border bg-background px-3 mt-1"
                    value={grupoSelecionado}
                    onChange={(e) =>
                      selecionarGrupo(e.target.value)
                    }
                  >
                    <option value="">
                      Selecione o grupo
                    </option>

                    {gruposDisponiveis.map((grupo) => (
                      <option
                        key={grupo}
                        value={grupo}
                      >
                        {grupo}
                      </option>
                    ))}
                  </select>
                </div>

                {grupoSelecionado && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-medium">
                        Grupo {grupoSelecionado}
                      </div>

                      <div className="text-sm text-zinc-400">
                        {atletasDoGrupo.length} atleta(s)
                      </div>
                    </div>

                    {atletasDoGrupo.length === 0 ? (
                      <div className="border rounded-lg p-4 text-sm text-zinc-400">
                        Nenhum atleta deste grupo está disponível agora.
                      </div>
                    ) : (
                      <div className="grid sm:grid-cols-2 gap-2">
                        {atletasDoGrupo.map((atleta) => {
                          const id = idAtleta(atleta);

                          const marcado =
                            selecionados.includes(id);

                          return (
                            <button
                              type="button"
                              key={id}
                              onClick={() =>
                                alternarAtleta(atleta)
                              }
                              className="border rounded-lg p-3 text-left flex items-center gap-3"
                            >
                              <div
                                className={
                                  marcado
                                    ? 'w-6 h-6 rounded bg-emerald-500 flex items-center justify-center'
                                    : 'w-6 h-6 rounded border border-zinc-600'
                                }
                              >
                                {marcado && (
                                  <Check className="w-4 h-4 text-white" />
                                )}
                              </div>

                              <div className="min-w-0">
                                <div className="font-medium truncate">
                                  {nomeAtleta(atleta)}
                                </div>

                                <div className="text-xs text-zinc-400">
                                  {atleta.ano_nascimento
                                    ? `Nasc. ${atleta.ano_nascimento}`
                                    : 'Ano não informado'}

                                  {' • '}

                                  {(atleta.situacao || atleta.status) === 'avaliado'
                                    ? 'Reavaliação'
                                    : 'Pendente'}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <Button
              onClick={criarRodada}
              disabled={
                acao ||
                selecionados.length === 0
              }
            >
              {acao ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ClipboardCheck className="w-4 h-4 mr-2" />
              )}

              {grupoSelecionado
                ? `Criar rodada — ${grupoSelecionado} (${selecionados.length})`
                : `Criar rodada com ${selecionados.length} atleta(s)`}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
